import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { kidService } from '../../services/api';

// État initial
const initialState = {
  kids: [],
  currentKid: null,
  loading: false,
  error: null,
  total: 0
};

// Actions asynchrones
export const fetchKids = createAsyncThunk(
  'kids/fetchKids',
  async (_, { rejectWithValue }) => {
    try {
      const response = await kidService.getAll();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchKidById = createAsyncThunk(
  'kids/fetchKidById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await kidService.getById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createKid = createAsyncThunk(
  'kids/createKid',
  async (kidData, { rejectWithValue }) => {
    try {
      const response = await kidService.create(kidData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateKid = createAsyncThunk(
  'kids/updateKid',
  async ({ id, ...kidData }, { rejectWithValue }) => {
    try {
      const response = await kidService.update(id, kidData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteKid = createAsyncThunk(
  'kids/deleteKid',
  async (id, { rejectWithValue }) => {
    try {
      await kidService.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Slice
const kidSlice = createSlice({
  name: 'kid',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentKid: (state, action) => {
      state.currentKid = action.payload;
    },
    clearCurrentKid: (state) => {
      state.currentKid = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all kids
      .addCase(fetchKids.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchKids.fulfilled, (state, action) => {
        state.loading = false;
        state.kids = action.payload.data || action.payload;
        state.total = action.payload.total || state.kids.length;
        state.error = null;
      })
      .addCase(fetchKids.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch kid by ID
      .addCase(fetchKidById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchKidById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentKid = action.payload;
        state.error = null;
      })
      .addCase(fetchKidById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create kid
      .addCase(createKid.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createKid.fulfilled, (state, action) => {
        state.loading = false;
        state.kids.push(action.payload);
        state.currentKid = action.payload;
        state.error = null;
      })
      .addCase(createKid.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update kid
      .addCase(updateKid.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateKid.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.kids.findIndex(kid => kid.id === action.payload.id);
        if (index !== -1) {
          state.kids[index] = action.payload;
        }
        if (state.currentKid && state.currentKid.id === action.payload.id) {
          state.currentKid = action.payload;
        }
        state.error = null;
      })
      .addCase(updateKid.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete kid
      .addCase(deleteKid.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteKid.fulfilled, (state, action) => {
        state.loading = false;
        state.kids = state.kids.filter(kid => kid.id !== action.payload);
        if (state.currentKid && state.currentKid.id === action.payload) {
          state.currentKid = null;
        }
        state.error = null;
      })
      .addCase(deleteKid.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, setCurrentKid, clearCurrentKid } = kidSlice.actions;

export default kidSlice.reducer;
